import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n/LanguageContext";

const initial = { name: "", email: "", message: "" };

// Netlify Forms encodes payloads as application/x-www-form-urlencoded.
const encode = (data) =>
    Object.keys(data)
        .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k]))
        .join("&");

export default function ContactForm() {
    const [form, setForm] = useState(initial);
    const [botField, setBotField] = useState("");
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();

    const onChange = (e) =>
        setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();

        // Honeypot: if a bot filled the hidden field, silently succeed.
        if (botField) {
            setForm(initial);
            return;
        }

        setLoading(true);
        try {
            const res = await fetch("/", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: encode({
                    "form-name": "contact",
                    "bot-field": "",
                    ...form,
                }),
            });
            if (!res.ok) throw new Error(`Netlify submit failed: ${res.status}`);
            toast.success(t("contact.form.successToast"));
            setForm(initial);
        } catch (err) {
            toast.error(t("contact.form.errorToast"));
        } finally {
            setLoading(false);
        }
    };

    const inputCls =
        "w-full bg-white border border-border rounded-lg px-4 py-3 text-ink placeholder:text-ink-soft/50 focus:outline-none focus:ring-2 focus:ring-terracotta/30 focus:border-terracotta transition-all";
    const labelCls = "block text-sm font-medium text-ink mb-2";

    return (
        <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="grid gap-5"
            name="contact"
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
        >
            {/* Netlify form-name identifier (required for JS submissions) */}
            <input type="hidden" name="form-name" value="contact" />
            {/* Honeypot field: kept invisible/off-screen for real users. */}
            <p className="hidden" aria-hidden="true">
                <label>
                    Do not fill this out:{" "}
                    <input
                        name="bot-field"
                        value={botField}
                        onChange={(e) => setBotField(e.target.value)}
                        tabIndex="-1"
                        autoComplete="off"
                    />
                </label>
            </p>

            <div>
                <label htmlFor="ct-name" className={labelCls}>{t("contact.form.nameLabel")}</label>
                <input id="ct-name" name="name" type="text" required
                    value={form.name} onChange={onChange}
                    data-testid="contact-name-input" className={inputCls}
                    placeholder={t("contact.form.namePlaceholder")} />
            </div>
            <div>
                <label htmlFor="ct-email" className={labelCls}>{t("contact.form.emailLabel")}</label>
                <input id="ct-email" name="email" type="email" required
                    value={form.email} onChange={onChange}
                    data-testid="contact-email-input" className={inputCls + " font-email"}
                    placeholder={t("contact.form.emailPlaceholder")} />
            </div>
            <div>
                <label htmlFor="ct-message" className={labelCls}>{t("contact.form.messageLabel")}</label>
                <textarea id="ct-message" name="message" rows={6} required
                    value={form.message} onChange={onChange}
                    data-testid="contact-message-input" className={inputCls + " resize-none"}
                    placeholder={t("contact.form.messagePlaceholder")} />
            </div>
            <div>
                <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-button"
                    className="rounded-full bg-ink hover:bg-terracotta text-cream font-semibold px-8 py-4 transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {loading ? t("contact.form.submitting") : t("contact.form.submit")}
                </button>
            </div>
        </form>
    );
}
